import React, { useState, useContext, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Box,
} from '@mui/material';
import { toast } from 'react-toastify';
import DataContext from '../../../Contexts/DataContext';

/**
 * CreateEntityDialog — controlled MUI Dialog for creating a new entity inline
 * within the Pro Forma wizard.
 *
 * Props:
 *   open       {boolean}          — controls dialog visibility
 *   onClose    {() => void}       — called on cancel or backdrop click; no context call
 *   onCreated  {(entity) => void} — called with the created entity object on success
 */
const CreateEntityDialog = ({ open, onClose, onCreated }) => {
  const context = useContext(DataContext);

  const [form, setForm] = useState({ entity_id: '', entity_name: '' });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Reset form state whenever the dialog opens
  useEffect(() => {
    if (open) {
      setForm({ entity_id: '', entity_name: '' });
      setErrors({});
    }
  }, [open]);

  const validate = () => {
    const newErrors = {};
    const trimmedId = String(form.entity_id).trim();
    const trimmedName = String(form.entity_name).trim();

    if (!trimmedId) {
      newErrors.entity_id = 'Entity ID is required';
    } else if (!/^\d+$/.test(trimmedId)) {
      newErrors.entity_id = 'Entity ID must be numeric';
    } else {
      // Client-side duplicate check against existing entities in context
      const existingEntities = context.activeProjectAttributes?.entities || [];
      const isDuplicate = existingEntities.some(
        (e) => String(e.entity_id).trim() === trimmedId
      );
      if (isDuplicate) {
        newErrors.entity_id = `Entity ID ${trimmedId} already exists in this project`;
      }
    }

    if (!trimmedName) {
      newErrors.entity_name = 'Entity Name is required';
    }

    return newErrors;
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        entity_id: String(form.entity_id).trim(),
        entity_name: String(form.entity_name).trim(),
      };
      const result = await context.createEntity(payload);
      if (result) {
        onCreated(result);
        handleClose();
      } else {
        // Backend rejected — most likely a duplicate that slipped past the client check
        setErrors({ entity_id: `Entity ID ${payload.entity_id} already exists. Choose a different ID.` });
      }
    } catch (err) {
      console.error('Error creating entity:', err);
      toast.error('Failed to create entity. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setForm({ entity_id: '', entity_name: '' });
    setErrors({});
    onClose();
  };

  const handleFieldChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    // Clear the error for this field as the user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Entity</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField
            label="Entity ID *"
            size="small"
            fullWidth
            value={form.entity_id}
            onChange={handleFieldChange('entity_id')}
            error={!!errors.entity_id}
            helperText={errors.entity_id || 'Must be numeric (e.g. 100)'}
            disabled={saving}
            inputProps={{ inputMode: 'numeric' }}
          />
          <TextField
            label="Entity Name *"
            size="small"
            fullWidth
            value={form.entity_name}
            onChange={handleFieldChange('entity_name')}
            error={!!errors.entity_name}
            helperText={errors.entity_name}
            disabled={saving}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? 'Creating...' : 'Create Entity'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEntityDialog;
